import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { SCHEDULE_ATTR_ROWS } from '../lib/scheduleFields';
import { ArrowLeft, Download, FileText, FileDown, Printer, Pencil, GitMerge, GitBranch, Network } from 'lucide-react';
import FlowTreeView from '../components/FlowTreeView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_LOGO = "https://customer-assets.emergentagent.com/job_f6178015-27bd-4e5a-b6ad-e233ba6cad25/artifacts/di3lsdme_Logo.jpeg";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const DDRPreviewPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [ddrData, setDdrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [editTracing, setEditTracing] = useState(false);
  const [tracingSel, setTracingSel] = useState({});
  const [mergeSel, setMergeSel] = useState({});
  const [selectedForMerge, setSelectedForMerge] = useState([]);
  const [savingTracing, setSavingTracing] = useState(false);
  const [mergeBusy, setMergeBusy] = useState(false);
  const [showFlow, setShowFlow] = useState(false);

  useEffect(() => {
    fetchDDR();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const fetchDDR = async () => {
    try {
      const res = await api.get(`/ddr/preview/${caseId}`);
      setDdrData(res.data);
      return res.data;
    } catch (error) {
      toast.error('Failed to load DDR preview');
      navigate(`/cases/${caseId}`);
    } finally {
      setLoading(false);
    }
  };

  const initTracingState = (data) => {
    const single = {}, merge = {};
    (data?.tracing || []).forEach((t) => {
      if (t.is_merge) {
        if (t.available_templates?.length > 0) merge[t.merge_group_id] = t.selected_template_id || t.available_templates[0].id;
      } else if (t.available_templates?.length > 0) {
        single[t.entry_id] = t.selected_template_id || t.available_templates[0].id;
      }
    });
    setTracingSel(single);
    setMergeSel(merge);
    setSelectedForMerge([]);
  };

  const handleExport = async (format, withBlanks = true) => {
    setExporting(true);
    try {
      const response = await api.post('/ddr/generate',
        { case_id: caseId, format, with_blanks: withBlanks },
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const suffix = withBlanks ? '' : '_no_blanks';
      link.setAttribute('download', `DDR_${ddrData?.case?.case_name?.replace(/\s+/g, '_') || 'report'}${suffix}.${format === 'pdf' ? 'pdf' : 'docx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${format.toUpperCase()} exported${withBlanks ? '' : ' (without blanks)'}`);
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const openEditTracing = () => {
    initTracingState(ddrData);
    setEditTracing(true);
  };

  const saveTracing = async () => {
    setSavingTracing(true);
    try {
      const origS = {}, origM = {};
      (ddrData?.tracing || []).forEach((t) => {
        if (t.is_merge) origM[t.merge_group_id] = t.selected_template_id;
        else origS[t.entry_id] = t.selected_template_id;
      });
      const changedS = Object.entries(tracingSel).filter(([id, tid]) => origS[id] !== tid);
      const changedM = Object.entries(mergeSel).filter(([gid, tid]) => origM[gid] !== tid);
      await Promise.all([
        ...changedS.map(([id, tid]) => api.put(`/document-entries/${id}`, { tracing_template_id: tid })),
        ...changedM.map(([gid, tid]) => api.put(`/cases/${caseId}/tracing-merges/${gid}`, { merge_template_id: tid })),
      ]);
      await fetchDDR();
      toast.success('Tracing updated');
      setEditTracing(false);
    } catch (e) {
      toast.error('Failed to update tracing');
    } finally {
      setSavingTracing(false);
    }
  };

  const doMerge = async () => {
    if (selectedForMerge.length < 2) return;
    setMergeBusy(true);
    try {
      const res = await api.post(`/cases/${caseId}/tracing-merges`, { entry_ids: selectedForMerge });
      if (res.data.matched) {
        toast.success('Documents merged into a single tracing paragraph');
      } else {
        toast.warning('No merge template found for this combination — documents will remain individual. Add a matching Merge Template in Admin to combine them.');
      }
      const data = await fetchDDR();
      initTracingState(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Merge failed');
    } finally {
      setMergeBusy(false);
    }
  };

  const doUnmerge = async (groupId) => {
    setMergeBusy(true);
    try {
      await api.delete(`/cases/${caseId}/tracing-merges/${groupId}`);
      toast.success('Merge removed');
      const data = await fetchDDR();
      initTracingState(data);
    } catch (e) {
      toast.error('Unmerge failed');
    } finally {
      setMergeBusy(false);
    }
  };

  const toggleMergeSelect = (entryId) => {
    setSelectedForMerge((s) => s.includes(entryId) ? s.filter((x) => x !== entryId) : [...s, entryId]);
  };

  const renderRich = (text) =>
    (text || '')
      .split(/(<b>[^<]*<\/b>|\(excluding [^)]+\))/)
      .map((part, i) => {
        if (/^<b>.*<\/b>$/.test(part)) {
          const inner = part.slice(3, -4).replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
          return <strong key={i}>{inner}</strong>;
        }
        if (/^\(excluding /.test(part)) return <em key={i}>{part}</em>;
        return <span key={i}>{part}</span>;
      });

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="h-96 bg-slate-200 rounded-lg" />
      </div>
    );
  }

  const { branding, entries, tracing } = ddrData || {};
  const tracingWithText = (tracing || []).filter((t) => t.paragraph);

  return (
    <div className="space-y-6" data-testid="ddr-preview-page">
      {/* Header - Hidden in print */}
      <div className="flex items-center justify-between print:hidden">
        <div className="flex items-center gap-4">
          <Link to={`/cases/${caseId}`}>
            <Button variant="ghost" size="icon">
              <ArrowLeft size={20} />
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-[#1e3a5f]" style={{ fontFamily: 'Playfair Display, serif' }}>
            DDR Preview
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handlePrint}>
            <Printer size={18} className="mr-2" />
            Print
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="bg-[#1e3a5f] hover:bg-[#162c46]" disabled={exporting} data-testid="export-btn">
                <Download size={18} className="mr-2" />
                {exporting ? 'Exporting...' : 'Export'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-64">
              <DropdownMenuItem onClick={() => handleExport('pdf', true)} data-testid="export-pdf-with-blanks">
                <FileDown size={16} className="mr-2" />
                Download PDF (With Blanks)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pdf', false)} data-testid="export-pdf-without-blanks">
                <FileDown size={16} className="mr-2" />
                Download PDF (Without Blanks)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('docx', true)} data-testid="export-docx-with-blanks">
                <FileText size={16} className="mr-2" />
                Download Word (With Blanks)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('docx', false)} data-testid="export-docx-without-blanks">
                <FileText size={16} className="mr-2" />
                Download Word (Without Blanks)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* DDR Document */}
      <Card className="border-0 shadow-lg max-w-4xl mx-auto" data-testid="ddr-document">
        <CardContent
          className="p-8 md:p-12"
          style={{ fontFamily: '"Times New Roman", Times, serif', fontSize: '12pt', lineHeight: 1.5 }}
        >
          {/* First-page letterhead (full banner with address) */}
          {branding?.letterhead_first_url && (
            <div className="mb-6" data-testid="ddr-letterhead">
              <img
                src={branding.letterhead_first_url.startsWith('http') ? branding.letterhead_first_url : `${BACKEND_URL}${branding.letterhead_first_url}`}
                alt="Letterhead"
                className="w-full object-contain"
              />
            </div>
          )}

          {/* Date */}
          <p className="font-bold text-slate-900 mb-4" data-testid="ddr-date">
            {ddrData?.case?.report_date || new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: '2-digit' })}
          </p>

          {/* To, + recipient name + multi-line address */}
          <div className="mb-4" data-testid="ddr-addressee">
            <p className="text-slate-900">To,</p>
            {ddrData?.case?.client_name && <p className="text-slate-900">{ddrData.case.client_name}</p>}
            {(ddrData?.case?.addressee_address || '').split('\n').map((line, i) =>
              line.trim() ? <p key={i} className="text-slate-900">{line}</p> : null
            )}
          </div>

          {/* Static Subject line */}
          <p className="mb-8 text-slate-900" data-testid="ddr-subject">
            Sub: - Due Diligence Report for the below mentioned Schedule Property:-
          </p>

          {/* Schedule Property Section (case level) — before I. Documents Furnished */}
          {(ddrData?.case?.schedule_property || []).length > 0 && (
            <div className="mb-8" data-testid="ddr-schedule-property">
              <h4 className="font-bold text-[#1e3a5f] text-base mb-4 uppercase">Schedule Property</h4>
              {ddrData.case.schedule_property.map((item, idx) => (
                <div key={idx} className="mb-6" data-testid={`schedule-item-${idx + 1}`}>
                  <p className="font-bold text-slate-800 mb-1">ITEM NO. {idx + 1}</p>
                  {item.description && <p className="text-slate-800 leading-relaxed mb-2 text-justify">{item.description}</p>}
                  <div className="text-slate-800 mb-3 space-y-0.5">
                    {[['East By', item.east_by], ['West By', item.west_by], ['North By', item.north_by], ['South By', item.south_by]]
                      .filter(([, v]) => v).map(([l, v]) => (
                        <div key={l}><span className="font-semibold">{l}:</span> {v}</div>
                      ))}
                  </div>
                  <table className="w-full border-collapse text-sm">
                    <tbody>
                      {SCHEDULE_ATTR_ROWS.map(([key, label]) => (
                        <tr key={key} className="align-top">
                          <td className="border border-slate-400 px-3 py-1.5 font-semibold text-slate-700 bg-slate-50 w-2/5">{label}</td>
                          <td className="border border-slate-400 px-3 py-1.5 text-slate-900">{item[key] || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}

          {/* Documents Section */}
          <div className="mb-8">
            <h4 className="font-bold text-[#1e3a5f] text-base mb-4 uppercase">
              I. Documents Furnished:
            </h4>
            
            {entries?.length > 0 ? (
              <table className="w-full border-collapse text-sm" data-testid="ddr-table">
                <thead>
                  <tr>
                    <th className="border border-slate-400 px-3 py-2.5 text-center font-bold text-[#1e3a5f] bg-slate-50 w-16">SL NO</th>
                    <th className="border border-slate-400 px-3 py-2.5 text-center font-bold text-[#1e3a5f] bg-slate-50 w-28">DATE</th>
                    <th className="border border-slate-400 px-3 py-2.5 text-left font-bold text-[#1e3a5f] bg-slate-50">DESCRIPTION OF THE DOCUMENTS</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry, idx) => (
                    entry.is_item ? (
                      <tr key={`item-${idx}`} className="align-top" data-testid={`ddr-item-heading-${idx}`}>
                        <td colSpan={3} className="border border-slate-400 px-3 py-2 text-center font-bold text-slate-900">
                          {entry.description}
                        </td>
                      </tr>
                    ) : (
                    <tr key={entry.serial_number} className="align-top" data-testid={`ddr-entry-${entry.serial_number}`}>
                      <td className="border border-slate-400 px-3 py-2 text-center text-slate-800">{entry.serial_number}</td>
                      <td className="border border-slate-400 px-3 py-2 text-center text-slate-800 whitespace-nowrap">{entry.date || ''}</td>
                      <td className="border border-slate-400 px-3 py-2 leading-relaxed text-slate-800 text-justify">
                        {(entry.description || entry.rendered_text || '')
                          // Split on bold OR italic markers (keep them in the array)
                          .split(/(<b>[^<]*<\/b>|<i>[^<]*<\/i>|\(excluding [^)]+\))/)
                          .map((part, i) => {
                            if (/^<b>.*<\/b>$/.test(part)) {
                              const inner = part.slice(3, -4)
                                .replace(/&lt;/g, '<')
                                .replace(/&gt;/g, '>')
                                .replace(/&amp;/g, '&');
                              return <strong key={i}>{inner}</strong>;
                            }
                            if (/^<i>.*<\/i>$/.test(part)) {
                              const inner = part.slice(3, -4)
                                .replace(/&lt;/g, '<')
                                .replace(/&gt;/g, '>')
                                .replace(/&amp;/g, '&');
                              return <em key={i}>{inner}</em>;
                            }
                            if (/^\(excluding /.test(part)) return <em key={i}>{part}</em>;
                            return <span key={i}>{part}</span>;
                          })}
                        {entry.remarks && (
                          <p className="text-xs text-slate-500 italic mt-1">Remarks: {entry.remarks}</p>
                        )}
                      </td>
                    </tr>
                    )
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-slate-500 italic">No documents furnished.</p>
            )}
          </div>

          {/* II. Tracing of Title Section — paragraph format */}
          <div className="mb-8" data-testid="ddr-tracing-section">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-[#1e3a5f] text-base uppercase">
                II. Tracing of Title:
              </h4>
              <Button
                variant="outline" size="sm" onClick={openEditTracing}
                className="print:hidden" data-testid="edit-tracing-btn"
              >
                <Pencil size={14} className="mr-1.5" /> Edit Tracing
              </Button>
            </div>

            {tracingWithText.length > 0 ? (
              <div className="space-y-4" data-testid="tracing-paragraphs">
                {tracingWithText.map((t, idx) => (
                  <div key={t.entry_id} data-testid={`tracing-para-${idx + 1}`} className="flex gap-2">
                    <span className="font-semibold text-slate-800 shrink-0">{idx + 1}.</span>
                    <div className="flex-1">
                      <p className="text-slate-800 leading-relaxed text-justify">
                        {renderRich(t.paragraph)}
                      </p>
                      {(t.remarks_list || []).length === 1 && (
                        <p className="text-sm text-slate-500 italic mt-1">Remarks: {t.remarks_list[0]}</p>
                      )}
                      {(t.remarks_list || []).length > 1 && (
                        <div className="text-sm text-slate-500 italic mt-1">
                          <span>Remarks:</span>
                          <ol className="list-decimal ml-6">
                            {t.remarks_list.map((r, ri) => <li key={ri}>{r}</li>)}
                          </ol>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 italic">
                No tracing templates configured for these documents yet. Add tracing templates to the
                relevant Document Types (Admin → Document Types) to auto-generate this section.
              </p>
            )}
          </div>

          {/* Signature Section */}
          <div className="mt-16 pt-8 border-t border-slate-200">
            <div className="text-right">
              <p className="text-sm text-slate-700 font-medium">
                {branding?.firm_name || 'Advocates & Legal Consultants'}
              </p>
              <p className="text-xs text-slate-500 mt-1">Date: {new Date().toLocaleDateString('en-IN')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ================= OWNERSHIP FLOW (separate from the DDR document) ================= */}
      <Card className="mt-8 border-0 shadow-sm" data-testid="ddr-ownership-flow-section">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <div>
            <CardTitle className="text-lg text-[#1e3a5f] flex items-center gap-2">
              <GitBranch size={18} /> Ownership Flow
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Read-only legal title chain, shown separately from the DDR report. Built in the Ownership Flow Builder.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={showFlow ? 'outline' : 'default'}
              className={showFlow ? '' : 'bg-[#1e3a5f] hover:bg-[#162c46]'}
              onClick={() => setShowFlow((v) => !v)}
              data-testid="ddr-view-flow-btn"
            >
              <Network size={16} className="mr-1.5" />
              {showFlow ? 'Hide Ownership Flow' : 'View Ownership Flow'}
            </Button>
            <Button variant="outline" onClick={() => navigate(`/cases/${caseId}/ownership-flow`)} data-testid="ddr-open-flow-builder-btn">
              Open Builder
            </Button>
          </div>
        </CardHeader>
        {showFlow && (
          <CardContent>
            <FlowTreeView caseId={caseId} heightClass="h-[60vh]" />
          </CardContent>
        )}
      </Card>

      {/* Edit Tracing dialog — template selection + merge/unmerge */}
      <Dialog open={editTracing} onOpenChange={setEditTracing}>
        <DialogContent className="max-w-2xl max-h-[82vh] overflow-y-auto" data-testid="edit-tracing-dialog">
          <DialogHeader>
            <DialogTitle>Edit Tracing of Title</DialogTitle>
            <DialogDescription>
              Choose a template per paragraph, or select 2+ individual documents and merge them into one paragraph.
              Template changes apply on Save; merge/unmerge apply immediately.
            </DialogDescription>
          </DialogHeader>

          {(() => {
            const items = tracing || [];
            const mergeItems = items.filter((t) => t.is_merge);
            const freeSingles = items.filter((t) => !t.is_merge && !t.merge_group_id);
            const fallbackGroups = {};
            items.filter((t) => !t.is_merge && t.merge_group_id).forEach((t) => {
              (fallbackGroups[t.merge_group_id] = fallbackGroups[t.merge_group_id] || []).push(t);
            });

            return (
              <div className="space-y-5 py-2">
                {/* Merged paragraphs */}
                {mergeItems.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Merged Paragraphs</p>
                    {mergeItems.map((t) => (
                      <div key={t.merge_group_id} className="border rounded-lg p-3 bg-blue-50/40" data-testid={`merge-row-${t.merge_group_id}`}>
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <span className="text-sm font-semibold text-slate-800">
                            {t.document_type} <span className="text-xs text-slate-400">(Doc Nos. {t.serials.join(', ')})</span>
                          </span>
                          <div className="flex items-center gap-2">
                            {t.available_templates?.length > 0 && (
                              <Select value={mergeSel[t.merge_group_id] || ''}
                                onValueChange={(v) => setMergeSel((s) => ({ ...s, [t.merge_group_id]: v }))}>
                                <SelectTrigger className="w-44" data-testid={`merge-template-select-${t.merge_group_id}`}>
                                  <SelectValue placeholder="Template" />
                                </SelectTrigger>
                                <SelectContent>
                                  {t.available_templates.map((tmpl) => (
                                    <SelectItem key={tmpl.id} value={tmpl.id}>{tmpl.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                            <Button variant="outline" size="sm" disabled={mergeBusy}
                              onClick={() => doUnmerge(t.merge_group_id)} data-testid={`unmerge-btn-${t.merge_group_id}`}>
                              Unmerge
                            </Button>
                          </div>
                        </div>
                        <p className="text-xs text-slate-500 italic line-clamp-2">
                          {(t.paragraph || '').replace(/<\/?b>/g, '')}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Fallback groups (merged but no matching template) */}
                {Object.entries(fallbackGroups).map(([gid, rows]) => (
                  <div key={gid} className="border border-amber-300 rounded-lg p-3 bg-amber-50/50" data-testid={`fallback-group-${gid}`}>
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <span className="text-sm font-semibold text-amber-800">
                        Merge pending: {rows.map((r) => r.document_type).join(' + ')}
                      </span>
                      <Button variant="outline" size="sm" disabled={mergeBusy}
                        onClick={() => doUnmerge(gid)} data-testid={`unmerge-btn-${gid}`}>
                        Unmerge
                      </Button>
                    </div>
                    <p className="text-xs text-amber-700">
                      No merge template matches this combination yet — showing documents individually. Create a matching
                      Merge Template under Admin → Merge Templates to combine them.
                    </p>
                  </div>
                ))}

                {/* Individual documents (selectable for merge) */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Individual Documents</p>
                    <Button size="sm" className="bg-[#1e3a5f] hover:bg-[#162c46]"
                      disabled={selectedForMerge.length < 2 || mergeBusy}
                      onClick={doMerge} data-testid="merge-selected-btn">
                      <GitMerge size={14} className="mr-1.5" />
                      Merge Selected ({selectedForMerge.length})
                    </Button>
                  </div>

                  {freeSingles.length === 0 && (
                    <p className="text-sm text-slate-500">No individual documents available to merge.</p>
                  )}

                  {freeSingles.map((t) => (
                    <div key={t.entry_id} className="border rounded-lg p-3 flex items-start gap-3" data-testid={`tracing-edit-row-${t.entry_id}`}>
                      <Checkbox
                        className="mt-1"
                        checked={selectedForMerge.includes(t.entry_id)}
                        onCheckedChange={() => toggleMergeSelect(t.entry_id)}
                        data-testid={`merge-select-${t.entry_id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-3 mb-1">
                          <span className="text-sm font-semibold text-slate-800">
                            #{t.serials[0]} · {t.document_type}
                          </span>
                          {t.available_templates?.length > 0 && (
                            <Select value={tracingSel[t.entry_id] || ''}
                              onValueChange={(v) => setTracingSel((s) => ({ ...s, [t.entry_id]: v }))}>
                              <SelectTrigger className="w-44" data-testid={`tracing-template-select-${t.serials[0]}`}>
                                <SelectValue placeholder="Template" />
                              </SelectTrigger>
                              <SelectContent>
                                {t.available_templates.map((tmpl) => (
                                  <SelectItem key={tmpl.id} value={tmpl.id}>{tmpl.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 italic line-clamp-2">
                          {t.paragraph
                            ? (t.paragraph).replace(/<\/?b>/g, '')
                            : <span className="text-slate-400">No tracing template configured for this document type.</span>}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTracing(false)}>Close</Button>
            <Button className="bg-[#1e3a5f] hover:bg-[#162c46]" onClick={saveTracing} disabled={savingTracing} data-testid="save-tracing-btn">
              {savingTracing ? 'Saving…' : 'Save Templates'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DDRPreviewPage;
